import {
  lineItemResolutionSchema,
  type LineItemResolutionFormValues,
} from "@babackapp/return-widget-components/forms/line-item-resolution.form";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import type {
  Resolution,
  ResolutionOption,
} from "@babackapp/return-widget-components/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export type ResolutionOptionsFormContext =
  | { kind: "refund-or-discount" }
  | { kind: "exchange" };

export function useResolutionOptionsForm() {
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const updateLineItem = useUpdateLineItem(orderId);

  const draftLineItem = draft?.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );

  const resolution: Resolution | null = draftLineItem?.resolution ?? null;

  const isExchange =
    resolution === "exchange" || resolution === "exchange-credit";
  const context: ResolutionOptionsFormContext = isExchange
    ? { kind: "exchange" }
    : { kind: "refund-or-discount" };

  const defaultResolution = isExchange
    ? (resolution ?? "exchange")
    : (resolution ?? "refund");

  const form = useForm<LineItemResolutionFormValues>({
    resolver: zodResolver(lineItemResolutionSchema),
    defaultValues: {
      resolution: defaultResolution,
      resolutionOption:
        (draftLineItem?.resolutionOption as ResolutionOption | null) ?? null,
      exchangeVariantId: draftLineItem?.exchangeVariantId ?? null,
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (context.kind === "exchange") {
      updateLineItem(lineItemId, {
        resolution: values.resolution as "exchange" | "exchange-credit",
      });
    } else {
      updateLineItem(lineItemId, {
        resolution:
          values.resolutionOption === "discount-code" ? "discount" : "refund",
        resolutionOption: values.resolutionOption,
      });
    }

    navigate("../confirmation");
  });

  return { form, onSubmit, updateLineItem, lineItemId, resolution, context };
}
